import { type ColumnDefinition } from '@turnpike/portal-data-grid';
import { useMemo } from 'react';

import { formatSendTime } from '../model/utils/date';

import { TextCell } from './components/TextCell';

import type { ActionModel } from '../model/types/action.model';
import type { ActionStatusModel } from '../model/types/actions-status.model';

import { useOrganizationsDictionary } from '@/features/Shared/hooks/useOrganizations';

interface Props {
	actionsStatuses?: Record<string, ActionStatusModel>;
}

export const useTableLayoutColumns = ({ actionsStatuses = {} }: Props) => {
	const organizations = useOrganizationsDictionary();

	const columns = useMemo((): ColumnDefinition<ActionModel>[] => {
		return [
			{
				key: 'name',
				title: 'Name',
				width: 220,
				draggable: false,
				render: (_value, row) => <TextCell isBold={true} text={row.title} />,
				sortFn(a, b) {
					return a.title.localeCompare(b.title);
				},
			},
			{
				key: 'sendTime',
				title: ' Time',
				align: 'center',
				width: 170,
				render: (_value, row) => (
					<TextCell isBold={false} text={formatSendTime(row.sendTime)} />
				),
				sortFn(a, b) {
					if (!a.sendTime) return 1;
					if (!b.sendTime) return -1;
					const dateA = new Date(a.sendTime).getTime();
					const dateB = new Date(b.sendTime).getTime();
					return dateA - dateB;
				},
			},
			{
				key: 'organizationId',
				title: 'Organization',
				width: 250,
				render: (_value, row) => (
					<TextCell text={organizations[row.organizationId]?.name ?? ''} />
				),
			},
			{
				key: 'sentTo',
				title: 'Sent To',
				width: 250,
				align: 'center',
				render: (_value, row) => (
					<TextCell text={actionsStatuses?.[row.id]?.sentUserIds?.length ?? '0'} />
				),
			},
			{
				key: 'acceptedBy',
				title: 'Accepted By',
				width: 250,
				align: 'center',
				render: (_value, row) => (
					<TextCell text={actionsStatuses?.[row.id]?.acceptedUserIds?.length ?? '0'} />
				),
			},
			{
				key: 'responseTime',
				title: 'Response Time',
				width: 250,
				align: 'center',
				render: (_value, row) => {
					const time = actionsStatuses?.[row.id]?.answeredTimeInMs
						? (actionsStatuses[row.id].answeredTimeInMs ?? 0)
						: 0;

					return (
						<TextCell text={`${time === 0 ? '-' : `${(time / 1000).toFixed(0)}s`}`} />
					);
				},
			},
		];
	}, [organizations, actionsStatuses]);

	return { columns };
};
